import { useEffect, useState } from 'react';

import { api } from '../../api/client';
import type { Invoice } from '../../api/types';
import { reportEvent } from '../../embed/host-bridge';
import { PadronCheckInfo } from './PadronCheckInfo';

const STATUS = ['', 'approved', 'rejected', 'draft', 'pending'];

// La fecha informada a ARCA; los comprobantes anteriores no la guardaron y muestran emittedAt, como antes.
function issueDate(inv: Invoice): string {
  const cbteFch = inv.receipt?.cbteFch;
  if (cbteFch) return `${cbteFch.slice(0, 4)}-${cbteFch.slice(4, 6)}-${cbteFch.slice(6, 8)}`;
  return (inv.emittedAt ?? inv.createdAt ?? '').slice(0, 10);
}

function StatusBadge({ status }: { status: string }) {
  const cls = ['approved', 'rejected', 'draft', 'pending'].includes(status) ? status : 'draft';
  return <span className={`badge ${cls}`}>{status}</span>;
}

export function InvoicesPage() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [selected, setSelected] = useState<Invoice | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (status) params['status'] = status;
      if (externalRef) params['externalReference'] = externalRef;
      const res = await api.listInvoices(params);
      setInvoices(res.resources ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function openDetail(urn: string) {
    try {
      setSelected(await api.getInvoice(urn));
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function download(urn: string) {
    try {
      const { url } = await api.downloadInvoice(urn);
      reportEvent('invoice:download', { urn, url });
      window.open(url, '_blank');
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div>
      <div className="card">
        <h3>Órdenes facturadas</h3>
        <div className="row">
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS.map((s) => (
              <option key={s} value={s}>
                {s || 'Todos los estados'}
              </option>
            ))}
          </select>
          <input
            placeholder="Referencia de orden"
            value={externalRef}
            onChange={(e) => setExternalRef(e.target.value)}
          />
          <button className="btn" onClick={load} disabled={loading}>
            {loading ? 'Cargando…' : 'Buscar'}
          </button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Comprobante</th>
              <th>Orden</th>
              <th>Receptor</th>
              <th>Total</th>
              <th>Estado</th>
              <th>CAE</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.urn} className="clickable" onClick={() => openDetail(inv.urn)}>
                <td>{issueDate(inv)}</td>
                <td>
                  {inv.receipt?.voucherClass ?? '-'} {inv.receipt?.kind === 'invoice' ? 'Factura' : inv.receipt?.kind}{' '}
                  {inv.receipt?.to ? `#${inv.receipt.to}` : ''}
                </td>
                <td>{inv.externalReference ?? '-'}</td>
                <td>{inv.receiver?.name ?? 'Consumidor Final'}</td>
                <td>${inv.amounts?.total}</td>
                <td>
                  <StatusBadge status={inv.status} />
                </td>
                <td>{inv.cae?.code ?? '-'}</td>
              </tr>
            ))}
            {!invoices.length && !loading && (
              <tr>
                <td colSpan={7} className="center">
                  Sin comprobantes
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="card">
          <h3>
            Detalle {selected.receipt?.voucherClass} #{selected.receipt?.to ?? '-'}
          </h3>
          <div className="row">
            <div>
              <div className="muted">Receptor</div>
              <div>{selected.receiver?.name ?? 'Consumidor Final'}</div>
            </div>
            <div>
              <div className="muted">Total</div>
              <div>${selected.amounts?.total}</div>
            </div>
            <div>
              <div className="muted">CAE</div>
              <div>{selected.cae?.code ?? '-'}</div>
            </div>
            <div>
              <div className="muted">Vto CAE</div>
              <div>{selected.cae?.expirationDate ?? '-'}</div>
            </div>
          </div>
          <PadronCheckInfo check={selected.receiver?.padronCheck} declared={selected.receiver?.ivaCondition} />
          <div className="row" style={{ marginTop: 12 }}>
            {/* El core solo entrega el PDF de comprobantes aprobados: sin CAE no es un comprobante válido. */}
            {selected.status === 'approved' ? (
              <button className="btn" onClick={() => download(selected.urn)}>
                Descargar PDF
              </button>
            ) : (
              <span className="muted">El PDF está disponible cuando el comprobante está aprobado.</span>
            )}
            <button className="btn secondary" onClick={() => setSelected(null)}>
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
